<template>
  <q-page class="q-pa-sm">
    <breadcrumbs />
    <div>
      <q-input
        v-model="searchTerms"
        dense
        label="Search settings..."
        input-class="allow-global-search"
        autofocus
      />
      <transition-group name="list-complete">
        <menu-item-category
          v-for="category in filteredList"
          :key="category.label"
          :label="category.label"
        >
          <menu-item-card
            v-for="item in category.entries"
            :key="item.label"
            :item="item"
            default-icon="settings"
            @click="selectItem(item)"
          />
        </menu-item-category>
      </transition-group>
    </div>
    <online-qr-code-generator-dialog v-model="showQrCodeGeneratorDialog" />
  </q-page>
</template>

<script>
import i18n from 'src/i18n'
import Model from 'src/models/model'
import Breadcrumbs from 'src/modules/admin/components/Breadcrumbs'
import AdminMenuMixin, { LIBRARY_LEVEL } from 'src/modules/admin/menu/AdminMenuMixin'
import MenuItemCard from 'src/modules/admin/menu/MenuItemCard'
import MenuItemCategory from 'src/modules/admin/menu/MenuItemCategory'
import OnlineQrCodeGeneratorDialog from 'src/modules/admin/site/OnlineQrCodeGeneratorDialog'

/**
 * @typedef {import("./AdminMenuMixin").MenuItem} MenuItem
 * @type {MenuItem[]}
 */
export const settingsMenuItems = [
  {
    label: 'Circuit',
    circuit: true,
    entries: [
      {
        indent: 1,
        route: 'circuit-settings',
        label: 'Circuit Configuration',
        icon: 'apartment',
        description: 'Manage configuration that applies to the entirety of your circuit.',
        requiresPermission: Model.permissions.Circuit.View
      },
      {
        indent: 1,
        route: 'showing-badges',
        label: 'Showing Badges',
        icon: 'info',
        description: 'Manage badges used to control and communicate features of a showing for your customers.',
        requiresAnyPermission: [Model.permissions.Circuit.View, Model.permissions.FilmProgramming.View, Model.permissions.Ticketing.View]
      },
      {
        indent: 1,
        route: 'user-classes',
        label: 'User Classes',
        icon: 'groups',
        description: 'Manage types of users as the basis for available ticket types and memberships.',
        requiresPermission: Model.permissions.Circuit.View
      },
      {
        indent: 1,
        route: 'taxes',
        label: i18n.global.t('pricing.tax') + ' Types',
        icon: 'toll',
        description: `Create and manage ${i18n.global.t('pricing.tax')} types used for concession items, tickets, memberships, and more.`,
        requiresPermission: Model.permissions.Circuit.View
      },
      {
        indent: 1,
        route: 'site-location-zones',
        label: 'Location Zones',
        icon: 'grid_on',
        description: 'Manage Location Zones that group Sites together in location select page.',
        requiresPermission: Model.permissions.Circuit.View
      },
      {
        indent: 1,
        route: 'price-tiers',
        label: 'Price Tiers',
        icon: 'stacked_bar_chart',
        description: 'Manage your price tiers types to define pricing across sites.',
        requiresPermission: Model.permissions.Circuit.View,
        hideUnless: 'priceTierSettingsAreEnabled'
      },
      {
        indent: 1,
        route: 'third-party-rewards',
        label: 'Third Party Rewards',
        icon: 'fas fa-gift',
        requiresPermission: Model.permissions.ThirdPartyRewardConfig.View,
        description: 'Manage third party reward configs.'
      }
    ]
  },
  {
    label: 'Site',
    description: '',
    route: 'site',
    libraryLevel: LIBRARY_LEVEL.ANY,
    entries: [
      {
        indent: 1,
        route: 'site-settings',
        label: 'Site Configuration',
        icon: 'account_balance',
        description: 'Manage configuration that applies to the entirety of your site.',
        libraryLevel: LIBRARY_LEVEL.ANY,
        requiresPermission: Model.permissions.Site.View
      },
      {
        indent: 1,
        route: 'screens',
        label: 'Screens',
        icon: 'fa fa-screen-users',
        description: 'Manage the screens used to show your movies, including selecting seat charts and showing badges.',
        libraryLevel: LIBRARY_LEVEL.ANY,
        requiresPermission: Model.permissions.Auditorium.View
      },
      {
        indent: 1,
        route: 'seat-charts',
        icon: 'event_seat',
        label: 'Seat Charts',
        description: 'Create and manage seat charts that define your auditorium. Set on a screen or individual showings.',
        libraryLevel: LIBRARY_LEVEL.ANY,
        requiresPermission: Model.permissions.Auditorium.View
      },
      {
        indent: 1,
        route: 'price-cards',
        label: 'Price Cards',
        icon: 'local_atm',
        description: 'Create and manage sets of tickets and packages sold for your showings. Set against a time slot, movie, screen, or individual showings.',
        libraryLevel: LIBRARY_LEVEL.ANY,
        requiresPermission: Model.permissions.Ticketing.View
      },
      {
        indent: 1,
        route: 'ticket-types',
        label: 'Ticket Types',
        icon: 'confirmation_number',
        description: 'Create and manage available tickets. Set prices, availability and reporting rules, and more.',
        libraryLevel: LIBRARY_LEVEL.ANY,
        requiresPermission: Model.permissions.Ticketing.View
      },
      {
        indent: 1,
        route: 'booking-fee-types',
        label: 'Booking Fees',
        icon: 'fa fa-hand-holding-dollar',
        description: 'Manage booking and convenience fees charged on orders or tickets by sales channel, showing badges, and more.',
        libraryLevel: LIBRARY_LEVEL.ANY,
        requiresPermission: Model.permissions.Site.View
      },
      {
        indent: 1,
        route: 'payment-types',
        label: 'Payment Types',
        icon: 'payments',
        description: 'Create and manage custom payment types for unusual payment processing and tracking (bank transfers, external invoice payments).',
        requiresPermission: Model.permissions.Site.View
      },
      {
        indent: 1,
        route: 'tip-settings',
        label: 'Tipping',
        icon: 'fa fa-money-check-dollar',
        description: 'Manage Tip Share Rules and Tip Pool Types used for distributing and assigning Tips to your staff.',
        libraryLevel: LIBRARY_LEVEL.ANY,
        requiresPermission: Model.permissions.Tipping.View
      },
      {
        indent: 1,
        route: 'print-templates',
        label: 'Print Templates',
        icon: 'print',
        description: 'Manage printing options for your order receipts, tickets, and kitchen slips, including formatting, grouping, font sizes, and custom content.',
        requiresPermission: Model.permissions.Site.View
      },
      {
        indent: 1,
        route: 'external-ticketing-clients',
        label: 'External Ticketing Clients',
        icon: 'rss_feed',
        description: 'Manage third party ticketing availability and sales rules.',
        circuit: true,
        requiresPermission: Model.permissions.Site.View
      },
      {
        indent: 1,
        label: 'Job Types',
        icon: 'plumbing',
        description: 'View and manage Employee Job Types, used with your time clock.',
        route: 'employee-job-types',
        libraryLevel: LIBRARY_LEVEL.ANY,
        requiresPermission: Model.permissions.Team.View
      }
    ]
  },
  {
    label: 'Promotions',
    libraryLevel: LIBRARY_LEVEL.ANY,
    circuit: true,
    entries: [
      {
        indent: 2,
        route: 'membership-types',
        label: 'Membership Types',
        icon: 'card_membership',
        description: 'Manage memberships available to your customers. Set rewards, with points, vouchers, discounts, and loyalty tiers.',
        requiresPermission: Model.permissions.Site.View
      },
      {
        indent: 2,
        route: 'discounts',
        label: 'Discounts',
        description: 'Manage promotional discounts. Set redemption rules, including specific items or showings. Redeem by code, membership, or more.',
        icon: 'money_off',
        libraryLevel: LIBRARY_LEVEL.ANY,
        requiresPermission: Model.permissions.Promotions.View
      },
      {
        indent: 2,
        route: 'voucher-types',
        label: 'Voucher Types',
        icon: 'sell',
        description: 'Manage promotional vouchers. Set redemption rules and issue by sale, points, membership, or service.',
        libraryLevel: LIBRARY_LEVEL.ANY,
        requiresPermission: Model.permissions.Promotions.View
      },
      {
        indent: 2,
        route: 'voucher-packages',
        label: 'Voucher Packages',
        icon: 'inventory_2',
        description: 'Manage voucher packages to sell many vouchers as a singular package. Use multiple redemptions or booklets.',
        libraryLevel: LIBRARY_LEVEL.ANY,
        requiresPermission: Model.permissions.Promotions.View
      },
      {
        indent: 1,
        route: 'packages',
        label: 'Packages',
        icon: 'luggage',
        description: 'Manage collections of items to be sold together, including tickets, items, or a combination (combos).',
        libraryLevel: LIBRARY_LEVEL.ANY,
        requiresPermission: Model.permissions.Promotions.View
      }
    ]
  },
  {
    label: 'Website and Marketing',
    description: '',
    libraryLevel: LIBRARY_LEVEL.ANY,
    entries: [
      {
        indent: 1,
        label: 'Theme and Style',
        icon: 'palette',
        description: 'Manage the look of your Website, App, and emails. Set your site colors, logos, and default styles.',
        route: 'site-theme',
        requiresAnyPermission: [Model.permissions.Site.View, Model.permissions.Marketing.View]
      },
      {
        indent: 1,
        label: 'Pages',
        icon: 'wysiwyg',
        description: 'Create and manage custom pages accessible from your Website and App.',
        route: 'pages',
        libraryLevel: LIBRARY_LEVEL.ANY,
        circuit: true,
        requiresPermission: Model.permissions.SiteContent.View
      },
      {
        indent: 1,
        label: 'Global Widgets',
        icon: 'fas fa-puzzle-piece',
        description: 'Create and manage widgets to be used across your Website and App.',
        route: 'global-widgets',
        libraryLevel: LIBRARY_LEVEL.ANY,
        circuit: true,
        requiresPermission: Model.permissions.SiteContent.View
      },
      {
        indent: 1,
        label: 'Custom Blocks',
        icon: 'fa fa-block',
        description: 'Create and manage content to reuse throughout your custom pages on your Website and App.',
        route: 'custom-blocks',
        libraryLevel: LIBRARY_LEVEL.ANY,
        circuit: true,
        requiresPermission: Model.permissions.SiteContent.View
      },
      {
        indent: 1,
        label: 'Navigations',
        icon: 'bookmark',
        description: 'Manage the navigation from the header, footer, and side menu of your Website and App.',
        route: 'navigations',
        libraryLevel: LIBRARY_LEVEL.ANY,
        circuit: true,
        requiresPermission: Model.permissions.SiteContent.View
      },
      {
        indent: 1,
        label: 'News Items',
        icon: 'post_add',
        description: 'Create and manage promotional content for your Website and App.',
        route: 'news-items',
        libraryLevel: LIBRARY_LEVEL.ANY,
        requiresPermission: Model.permissions.SiteContent.View
      },
      {
        indent: 1,
        label: 'News Item Classes',
        icon: 'bookmarks',
        description: 'Create news item classes for controlling the display of news items across your Website and App.',
        route: 'news-item-classes',
        libraryLevel: LIBRARY_LEVEL.ANY,
        requiresPermission: Model.permissions.SiteContent.View
      },
      {
        indent: 1,
        label: 'Broadcasts',
        icon: 'campaign',
        description: 'Create and send CustomerIO broadcast triggers.',
        route: 'broadcasts',
        customerio: true,
        circuit: true,
        requiresPermission: Model.permissions.Marketing.View
      },
      {
        indent: 1,
        label: 'Marketing Subscriptions',
        icon: 'insert_chart_outlined',
        description: 'Create and manage marketing subscription options.',
        route: 'marketing-subscriptions',
        requiresPermission: Model.permissions.Marketing.View
      },
      {
        indent: 1,
        label: 'Shortened Urls',
        icon: 'insert_chart_outlined',
        description: 'Create and manage short links to share or generate QR codes. Create easy sign up, order, or more links for sharing with your customers.',
        route: 'shortened-urls',
        requiresPermission: Model.permissions.Marketing.View
      }
    ]
  },
  {
    label: 'Programming',
    description: '',
    libraryLevel: LIBRARY_LEVEL.ANY,
    requiresPermission: Model.permissions.FilmProgramming.View,
    entries: [
      {
        route: 'movies',
        label: 'Movies',
        description: 'Create and manage movies, including posters, ratings, distribution terms and more. Shown on your Website, Register, and Signage.',
        icon: 'local_movies',
        libraryLevel: LIBRARY_LEVEL.ANY
      },
      {
        route: 'title-classes',
        label: 'Title Classes',
        description: 'Create and manage accounting categories for your movies. Use title classes to group and filter data in reports.',
        icon: 'category',
        libraryLevel: LIBRARY_LEVEL.ANY
      },
      {
        indent: 1,
        route: 'booking-weeks',
        label: 'Booking Weeks',
        icon: 'navigation',
        description: 'Create showing plans and schedule movies for coming weeks.',
        requiresPermission: Model.permissions.FilmProgramming.View
      },
      {
        indent: 1,
        route: 'plan-elements',
        label: 'Planning Elements',
        icon: 'calendar_view_day',
        description: 'Create and manage the building blocks of your booking weeks. Set ground rules for your booking weeks.'
      },
      {
        indent: 1,
        route: 'movie-sorting',
        label: 'Movie Sorting',
        icon: 'sort',
        libraryLevel: LIBRARY_LEVEL.ANY,
        description: 'Manage how movies are sorted in your Website and App.'
      }
    ]
  },
  {
    label: 'Studio Reporting',
    description: '',
    libraryLevel: LIBRARY_LEVEL.ANY,
    requiresPermission: Model.permissions.StudioReporting.View,
    entries: [
      {
        indent: 1,
        route: 'studios',
        label: 'Studios',
        libraryLevel: LIBRARY_LEVEL.ANY,
        icon: 'movie',
        description: 'Create and manage distribution studios used for generating Box Office Reports.'
      },
      {
        route: 'movie-terms',
        label: 'Movie Terms',
        description: 'Create and manage movie terms for your movies, used to set and adjust rental fees on your Box Office Reports.',
        icon: 'movie_edit',
        libraryLevel: LIBRARY_LEVEL.ANY
      }
    ]
  },
  {
    label: 'Non-Film Programming',
    description: '',
    hideUnless: 'areActivitiesEnabled',
    library: true,
    requiresPermission: Model.permissions.NonFilmProgramming.View,
    entries: [
      {
        route: 'activities',
        label: 'Activities',
        description: 'Create and edit the Activities to be programmed in your Activity Schedule.',
        icon: 'local_activity',
        library: true
      },
      {
        indent: 1,
        route: 'activity-spaces',
        label: 'Activity Spaces',
        icon: 'workspaces',
        description: 'Create the Activity Spaces where your Activities will be held.',
        library: true
      },
      {
        indent: 1,
        route: 'activity-layouts',
        label: 'Activity Layouts',
        icon: 'grid_view',
        description: 'Templates for your Activity Schedules.'
      },
      {
        route: 'activity-schedules',
        label: 'Activity Schedules',
        description: 'Program your activities in your Activity Schedule.',
        icon: 'navigation',
        library: true
      }
    ]
  },
  {
    label: 'Schedule Setup',
    description: '',
    circuit: true,
    entries: [
      {
        indent: 1,
        route: 'default-price-cards',
        label: 'Default Price Cards',
        icon: 'event_available',
        description: 'Manage price card defaults for given time-slots and days of the booking week.',
        requiresPermission: Model.permissions.Ticketing.View
      },
      {
        indent: 1,
        route: 'site-schedules',
        label: 'Site Schedules',
        icon: 'calendar_view_month',
        description: 'Manage opening hours for your site, and how your hours vary throughout the year.',
        requiresPermission: Model.permissions.Site.View
      }
    ]
  },
  {
    label: 'Concession',
    description: '',
    libraryLevel: LIBRARY_LEVEL.ANY,
    requiresPermission: Model.permissions.Concession.View,
    entries: [
      {
        indent: 1,
        route: 'items',
        label: 'Items',
        icon: 'fastfood',
        description: 'Create and manage items for sale. Set promotional material, variations (pepperoni or margherita), modifiers (extra cheese), and more.',
        libraryLevel: LIBRARY_LEVEL.ANY
      },
      {
        indent: 1,
        route: 'item-variation-price-tiers',
        label: 'Item Variation Price Tiers',
        icon: 'stacked_bar_chart',
        description: 'Manage tiered pricing for item variations, to manage pricing for multiple sites.',
        libraryLevel: LIBRARY_LEVEL.ANY,
        hideUnless: 'priceTierSettingsAreEnabled'
      },
      {
        indent: 1,
        route: 'item-classes',
        label: 'Item Classes',
        icon: 'workspaces',
        description: 'Create and manage accounting categories for your items. Use item classes to group and filter data in reports.'
      },
      {
        indent: 1,
        route: 'categories',
        label: 'Categories',
        icon: 'category',
        description: 'Create and manage categories for selling items on your Website and Registers.',
        libraryLevel: LIBRARY_LEVEL.ANY
      },
      {
        indent: 1,
        route: 'modifier-sets',
        label: 'Modifier Sets',
        icon: 'restaurant_menu',
        description: 'Organize modifiers into sets to be added to an item together (condiments, toppings, etc).',
        libraryLevel: LIBRARY_LEVEL.ANY
      },
      {
        indent: 1,
        route: 'modifiers',
        label: 'Modifiers',
        icon: 'fa fa-hotdog',
        description: 'Create and manage modifiers that can be applied to items through modifier sets (extra cheese, no pickles).',
        libraryLevel: LIBRARY_LEVEL.ANY
      },
      {
        indent: 1,
        route: 'concessions-preview',
        label: 'Concessions Preview',
        icon: 'fa fa-list',
        description: 'Preview your concessions availability across applications, sales channels, and more.',
        libraryLevel: LIBRARY_LEVEL.ANY
      }
    ]
  },
  {
    label: 'Kitchen and Delivery',
    description: '',
    libraryLevel: LIBRARY_LEVEL.ANY,
    entries: [
      {
        indent: 1,
        route: 'kitchen',
        label: 'Kitchen Displays',
        icon: 'monitor',
        description: 'Create and manage your Kitchen Displays for displaying items requiring preparation or delivery.',
        libraryLevel: LIBRARY_LEVEL.ANY,
        requiresAnyPermission: [Model.permissions.Kitchen.View, Model.permissions.PointOfSale.View]
      },
      {
        indent: 1,
        route: 'location',
        label: 'Locations',
        icon: 'kitchen',
        description: 'Manage locations for tracking item inventory and item preparation (bar, kitchen, espresso).',
        libraryLevel: LIBRARY_LEVEL.ANY,
        requiresAnyPermission: [Model.permissions.Kitchen.View, Model.permissions.PointOfSale.View, Model.permissions.Concession.View, Model.permissions.HardwareAndRegisterStation.ManagePrinterRouting]
      },
      {
        indent: 1,
        route: 'delivery',
        label: 'Delivery',
        icon: 'delivery_dining',
        description: 'Create and manage custom delivery types and locations.',
        libraryLevel: LIBRARY_LEVEL.ANY,
        requiresAnyPermission: [Model.permissions.Kitchen.View, Model.permissions.PointOfSale.View]
      },
      {
        indent: 1,
        label: 'Remote Order QR Code Generator',
        icon: 'qr_code',
        description: 'Generate QR Codes for delivery to tables, theater seats, or custom links.',
        method: 'showQrCodeGenerator',
        requiresAnyPermission: [Model.permissions.Kitchen.View, Model.permissions.PointOfSale.View, Model.permissions.Marketing.View]
      }
    ]
  },
  {
    label: 'Inventory',
    description: '',
    libraryLevel: LIBRARY_LEVEL.ANY,
    requiresPermission: Model.permissions.Inventory.View,
    entries: [
      {
        indent: 1,
        route: 'inventory-strategies',
        label: 'Inventory Strategies',
        icon: 'fal fa-boxes-stacked',
        description: 'Configure inventory tracking for your site.'
      },
      {
        indent: 1,
        route: 'inventory-suppliers',
        label: 'Inventory Suppliers',
        icon: 'kitchen',
        description: 'Create and manage suppliers of tracked inventory consumables.',
        libraryLevel: LIBRARY_LEVEL.ANY,
        hideUnless: 'inventorySettingsAreEnabled'
      },
      {
        indent: 1,
        route: 'inventory-items',
        label: 'Inventory Items',
        icon: 'fastfood',
        description: 'Create and manage inventory items to track consumable stocks. Sell inventory individually or as components of a recipe.',
        libraryLevel: LIBRARY_LEVEL.ANY,
        hideUnless: 'inventorySettingsAreEnabled'
      },
      {
        indent: 1,
        route: 'inventory-recipes',
        label: 'Inventory Recipes',
        icon: 'restaurant_menu',
        description: 'Create and manage recipes of inventory items that are used together (a bag and popcorn kernels on a popcorn sale).',
        libraryLevel: LIBRARY_LEVEL.ANY,
        hideUnless: 'inventorySettingsAreEnabled'
      }
    ]
  },
  {
    label: 'Stations and Hardware',
    description: '',
    libraryLevel: LIBRARY_LEVEL.ANY,
    entries: [
      {
        indent: 1,
        route: 'stations',
        label: 'Register & Kiosk Stations',
        icon: 'point_of_sale',
        description: 'Create and manage each of your physical employee-operated Registers or customer-operated Kiosks.',
        requiresPermission: Model.permissions.HardwareAndRegisterStation.View
      },
      {
        indent: 1,
        route: 'register-grids',
        label: 'Register Grids',
        icon: 'grid_on',
        description: 'Create and manage grids of buttons to use from your Registers. Buttons include items, promotions, and more.',
        libraryLevel: LIBRARY_LEVEL.ANY,
        requiresPermission: Model.permissions.HardwareAndRegisterStation.View
      },
      {
        indent: 1,
        route: 'hardwares',
        label: 'Remote Hardware',
        icon: 'devices_other',
        description: 'Manage hardware (Raspberry Pi computers) installed on site at Registers, Kiosks, Printers and Signage.',
        requiresPermission: Model.permissions.HardwareAndRegisterStation.View
      },
      {
        indent: 1,
        route: 'hardware-jobs',
        label: 'Remote Hardware Jobs',
        icon: 'work',
        description: 'Schedule and manage regular jobs run on your local hardware (TMS updates).',
        requiresPermission: Model.permissions.HardwareAndRegisterStation.View
      },
      {
        indent: 1,
        route: 'cash-trays',
        label: 'Cash Trays',
        icon: 'price_change',
        description: 'Create and manage your physical cash trays used for holding and counting cash at your Registers.',
        requiresAnyPermission: [Model.permissions.HardwareAndRegisterStation.View, Model.permissions.PointOfSale.View]
      }
    ]
  },
  {
    label: 'Signage',
    description: '',
    requiresPermission: Model.permissions.SiteContent.View,
    entries: [
      {
        indent: 1,
        route: 'signage-media-items',
        label: 'Signage Media',
        icon: 'cast_connected',
        description: 'Create and manage media used by your Signage.'
      },
      {
        indent: 1,
        route: 'signage-media-classes',
        label: 'Signage Media Classes',
        icon: 'filter_none',
        description: 'Create and manage groups of media to be used on different types of Signage.'
      }
    ]
  },
  {
    label: 'Reports',
    description: '',
    requiresPermission: Model.permissions.OperationsReporting.View,
    entries: [
      {
        indent: 1,
        route: 'report-categories',
        label: 'Report Categories',
        icon: 'category',
        description: 'Create and manage reporting categories.'
      },
      {
        indent: 1,
        route: 'report-schedules',
        label: 'Report Schedules',
        icon: 'event',
        description: 'Schedule report emails. Set reports to be emailed to you, your team, or others on a recurring basis.'
      }
    ]
  },
  {
    label: 'Accounting',
    description: '',
    libraryLevel: LIBRARY_LEVEL.ANY,
    requiresPermission: Model.permissions.Financial.View,
    entries: [
      {
        indent: 1,
        route: 'accounts',
        label: 'Chart of Accounts (Beta)',
        icon: 'account_tree',
        description: 'Create and manage accounting and journal entry tools.',
        libraryLevel: LIBRARY_LEVEL.ANY
      }
    ]
  },
  {
    label: 'Security',
    description: '',
    requiresPermission: Model.permissions.Security.View,
    entries: [
      {
        indent: 1,
        route: 'roles',
        label: 'Roles',
        icon: 'badge',
        description: "Manage Roles and Permissions. Assign roles to users and control your team's application access."
      }
    ]
  },
  {
    label: 'Integrations',
    description: '',
    route: 'integrations',
    hideUnless: 'isPolarisEnabled',
    requiresPermission: Model.permissions.Site.View,
    entries: [
      {
        indent: 1,
        route: 'polaris',
        label: 'Polaris',
        icon: 'rss_feed',
        description: 'Configure Polaris Stock and Concessions exports.',
        hideUnless: 'isPolarisEnabled'
      }
    ]
  }
]

export default {
  name: 'SettingsMenu',
  components: {
    Breadcrumbs,
    MenuItemCard,
    MenuItemCategory,
    OnlineQrCodeGeneratorDialog
  },
  mixins: [AdminMenuMixin],
  context: ['currentUser'],
  configuration: ['currentCircuit', 'currentSite'],
  data () {
    return {
      savingRoute: null,
      showQrCodeGeneratorDialog: false
    }
  },
  beforeCreate () {
    // Required by AdminMenuMixin, before immediate watcher
    this.menuItems = settingsMenuItems
    this.basePath = 'settings'
  },
  methods: {
    trackSelectItem (item) {
      this.$trackEvent(this.$indyUserEvents.SETTING_ITEM_SELECTED, { item })
    },
    showQrCodeGenerator () {
      this.showQrCodeGeneratorDialog = true
    },
    isPolarisEnabled () {
      return this.currentSite.polarisEnabled
    },
    areActivitiesEnabled () {
      return this.$isFeatureEnabled(this.$featureFlags.enable_activities)
    }
  },
  trackPage: true
}
</script>
